import { Settings2, Users, FileBarChart, LayoutDashboard, ShieldCheck } from "lucide-react";

export const TDS_START_PATH = "/settings/tds/start";
export const TDS_WORKSPACE_PATH = "/settings/tds/workspace";
export const TDS_GUIDE_PATH = "/settings/tds/guide";

export const TDS_TABS = [
  {
    id: "workspace",
    path: TDS_WORKSPACE_PATH,
    label: "TDS Workspace",
    shortLabel: "Workspace",
    desc: "Guided step-by-step TDS workflow",
    icon: LayoutDashboard,
    requiresRegime: true,
  },
  {
    id: "configuration",
    path: "/settings/tds/configuration",
    label: "TDS Configuration",
    shortLabel: "Configuration",
    desc: "Deductor info, tax regimes, calculation & Form 16 settings",
    icon: Settings2,
    requiresRegime: true,
  },
  {
    id: "employee-tax",
    path: "/settings/tds/employee-tax",
    label: "Employee Tax Setup",
    shortLabel: "Employee Tax",
    desc: "Declarations, proofs, approvals & tax computation",
    icon: Users,
    requiresRegime: true,
  },
  {
    id: "reports",
    path: "/settings/tds/reports",
    label: "TDS Reports",
    shortLabel: "Reports",
    desc: "Deduction register, projections & Form 16",
    icon: FileBarChart,
    requiresRegime: true,
  },
  {
    id: "compliance",
    path: "/settings/tds/compliance",
    label: "Compliance Center",
    shortLabel: "Compliance",
    desc: "Challans, quarterly returns, reconciliation & Form 16 tracking",
    icon: ShieldCheck,
    requiresRegime: true,
  },
];

/** Progressive workspace steps — filtered by selected tax regime */
export const TDS_WORKSPACE_STEPS = [
  {
    id: "employee-info",
    order: 1,
    title: "Employee Information",
    summary: "Set up deductor details, TAN, and organization tax settings.",
    actionLabel: "Open Configuration",
    path: "/settings/tds/configuration",
    guideSection: "configuration",
    regimes: ["old", "new"],
    icon: Settings2,
  },
  {
    id: "tax-projection",
    order: 2,
    title: "Tax Projection",
    summary: "Review employee tax registers and projected annual liability.",
    actionLabel: "View Employees",
    path: "/settings/tds/employee-tax",
    guideSection: "employee-tax",
    regimes: ["old", "new"],
    icon: Users,
  },
  {
    id: "investments",
    order: 3,
    title: "Investment Declarations",
    summary: "Collect and review 80C, 80D, and other Chapter VI-A declarations.",
    actionLabel: "Manage Declarations",
    path: "/settings/tds/employee-tax",
    guideSection: "investment-declaration",
    regimes: ["old"],
    icon: Users,
  },
  {
    id: "exemptions",
    order: 4,
    title: "Exemptions",
    summary: "Configure HRA, LTA, and other applicable exemptions.",
    actionLabel: "Review Exemptions",
    path: "/settings/tds/employee-tax",
    guideSection: "exemptions",
    regimes: ["old"],
    icon: Users,
  },
  {
    id: "proof-verification",
    order: 5,
    title: "Proof Verification",
    summary: "Verify uploaded investment proofs before payroll approval.",
    actionLabel: "Verify Proofs",
    path: "/settings/tds/employee-tax",
    guideSection: "proof-verification",
    regimes: ["old"],
    icon: Users,
  },
  {
    id: "tax-calculation",
    order: 6,
    title: "Tax Calculation",
    summary: "Understand how annual tax and monthly TDS are computed.",
    actionLabel: "View Computation",
    path: "/settings/tds/employee-tax",
    guideSection: "computation",
    regimes: ["old", "new"],
    icon: Users,
  },
  {
    id: "monthly-tds",
    order: 7,
    title: "Monthly TDS",
    summary: "Generate and review the monthly TDS deduction register.",
    actionLabel: "Generate Register",
    path: "/settings/tds/reports",
    guideSection: "monthly-tds",
    regimes: ["old", "new"],
    icon: FileBarChart,
  },
  {
    id: "reports",
    order: 8,
    title: "Reports",
    summary: "Access deduction register, tax projection, and summary reports.",
    actionLabel: "Open Reports",
    path: "/settings/tds/reports",
    guideSection: "reports",
    regimes: ["old", "new"],
    icon: FileBarChart,
  },
  {
    id: "form16",
    order: 9,
    title: "Form 16",
    summary: "Generate Part A & B for employees after year-end reconciliation.",
    actionLabel: "Generate Form 16",
    path: "/settings/tds/reports",
    guideSection: "form16",
    regimes: ["old", "new"],
    icon: FileBarChart,
  },
  {
    id: "challan",
    order: 10,
    title: "Challan Management",
    summary: "Record government tax deposits and map challans to employees.",
    actionLabel: "Manage Challans",
    path: "/settings/tds/compliance",
    guideSection: "challan-management",
    regimes: ["old", "new"],
    icon: ShieldCheck,
  },
  {
    id: "reconciliation",
    order: 11,
    title: "Reconciliation",
    summary: "Match deducted TDS with deposited amounts and resolve variances.",
    actionLabel: "Start Reconciliation",
    path: "/settings/tds/compliance",
    guideSection: "reconciliation",
    regimes: ["old", "new"],
    icon: ShieldCheck,
  },
  {
    id: "quarterly-return",
    order: 12,
    title: "Quarterly Return",
    summary: "Prepare and file Form 24Q for each quarter.",
    actionLabel: "Generate Form 24Q",
    path: "/settings/tds/compliance",
    guideSection: "quarterly-return",
    regimes: ["old", "new"],
    icon: ShieldCheck,
  },
];

export function getWorkspaceStepsForRegime(regime) {
  return TDS_WORKSPACE_STEPS.filter((step) => step.regimes.includes(regime)).sort(
    (a, b) => a.order - b.order
  );
}
